import React from 'react';
import Storage from '../../lib/Storage';

export default class EventEditorErrorBoundary extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            hasError: false,
        };

        this.hasReported = false;
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error('EventEditorErrorBoundary caught an error', error, info);

        this.reportError(error, info);

        if (typeof this.props.onError === 'function') {
            try {
                this.props.onError(error, info);
            } catch (callbackError) {
                console.warn('EventEditorErrorBoundary onError callback threw', callbackError);
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.hasError && this.state.hasError && this.props.resetKey !== prevProps.resetKey) {
            this.resetBoundary();
        }
    }

    resetBoundary = () => {
        this.hasReported = false;
        this.setState({ hasError: false });
    }

    handleRetry = () => {
        this.resetBoundary();
    }

    reportError(error, info) {
        if (this.hasReported) {
            return;
        }

        this.hasReported = true;

        const { game } = this.props;
        const payload = {
            message: error && error.message ? error.message : 'Unknown error',
            stack: error && error.stack ? error.stack : null,
            componentStack: info && info.componentStack ? info.componentStack : null,
            gameId: game && game.id ? game.id : null,
            eventCount: game && Array.isArray(game.events) ? game.events.length : null,
            timestamp: new Date().toISOString(),
        };

        const reportUrl = process.env.REACT_APP_ERROR_REPORT_URL;

        console.info('EventEditorErrorBoundary reporting error', payload);

        if (!reportUrl) {
            console.warn('EventEditorErrorBoundary: REACT_APP_ERROR_REPORT_URL env var not set; skipping remote error report.');
            return;
        }

        const finalReportUrl = this.buildReportUrl(reportUrl);

        if (!finalReportUrl) {
            console.warn('EventEditorErrorBoundary: final report URL is invalid; skipping remote error report.');
            return;
        }

        try {
            const body = JSON.stringify(payload);

            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                const blob = new Blob([body], { type: 'application/json' });
                navigator.sendBeacon(finalReportUrl, blob);
            } else if (typeof fetch === 'function') {
                fetch(finalReportUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body,
                }).catch((networkError) => {
                    console.warn('EventEditorErrorBoundary failed to send error report (fetch)', networkError);
                });
            }
        } catch (reportError) {
            console.warn('EventEditorErrorBoundary failed to send error report', reportError);
        }
    }

    buildReportUrl(baseUrl) {
        if (!baseUrl) {
            return null;
        }

        let token = null;

        try {
            token = Storage && typeof Storage.getToken === 'function' ? Storage.getToken() : null;
        } catch (storageError) {
            console.warn('EventEditorErrorBoundary: unable to read token from storage', storageError);
        }

        if (typeof URL === 'undefined') {
            if (token) {
                const separator = baseUrl.indexOf('?') >= 0 ? '&' : '?';
                return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
            }
            return baseUrl;
        }

        try {
            const url = new URL(baseUrl, typeof window !== 'undefined' ? window.location.origin : undefined);

            if (token) {
                url.searchParams.set('token', token);
            }

            return url.toString();
        } catch (urlError) {
            console.warn('EventEditorErrorBoundary: failed to construct report URL with token', urlError);

            if (token) {
                const separator = baseUrl.indexOf('?') >= 0 ? '&' : '?';
                return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
            }

            return baseUrl;
        }
    }

    renderFallback() {
        return (
            <div className="event-editor-error-boundary">
                <h2>Event editor failed to load</h2>
                <p>Something went wrong while rendering the event editor. The error has been logged for review.</p>
                <div className="btn-row-10"></div>
                <div className="btn left blue" onClick={this.handleRetry}>
                    <span>Try to reload editor</span>
                </div>
                {this.props.renderFallbackActions && this.props.renderFallbackActions()}
            </div>
        );
    }

    render() {
        if (this.state.hasError) {
            return this.renderFallback();
        }

        return this.props.children;
    }
}


